import { useState } from 'react';
import { api } from './api';
import OpenItemsPanel from './OpenItemsPanel';

const TrashIcon = () => (
  <svg width="15" height="15" viewBox="0 0 24 24" fill="none"
    stroke="#e57373" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round">
    <polyline points="3 6 5 6 21 6" />
    <path d="M19 6l-1 14H6L5 6" />
    <path d="M10 11v6M14 11v6" />
    <path d="M9 6V4h6v2" />
  </svg>
);

const CaretIcon = ({ open }) => (
  <svg width="10" height="10" viewBox="0 0 10 10" style={{
    marginRight: 6,
    transition: 'transform 0.15s',
    transform: open ? 'rotate(90deg)' : 'none',
  }}>
    <path d="M3 2 L7 5 L3 8" stroke="#737370" strokeWidth="1.5" fill="none" strokeLinecap="round" strokeLinejoin="round" />
  </svg>
);

export default function Projects({ projects, onProjectsChange }) {
  const [name, setName]       = useState('');
  const [rate, setRate]       = useState('');
  const [fee, setFee]         = useState('10');
  const [platform, setPlatform] = useState(true);
  const [error, setError]     = useState('');
  const [loading, setLoading] = useState(false);
  const [expanded, setExpanded] = useState(null);  // project id currently expanded

  const netRate = Math.round(parseFloat(rate || 0) * (1 - parseFloat(fee || 0) / 100));

  async function handleAdd(e) {
    e.preventDefault();
    if (!name.trim()) return setError('Project name is required');
    setError(''); setLoading(true);
    try {
      await api.createProject({
        name:             name.trim(),
        rate_per_hour:    parseFloat(rate)  || 0,
        platform_fee_pct: parseFloat(fee)   || 0,
        is_platform_work: platform,
      });
      setName(''); setRate(''); setFee('10'); setPlatform(true);
      onProjectsChange();
    } catch (err) {
      setError(err.message);
    } finally {
      setLoading(false);
    }
  }

  async function handleTogglePlatform(p) {
    try {
      await api.updateProject(p.id, {
        name:             p.name,
        rate_per_hour:    p.rate_per_hour,
        platform_fee_pct: p.platform_fee_pct,
        is_platform_work: !p.is_platform_work,
      });
      onProjectsChange();
    } catch (err) {
      setError(err.message);
    }
  }

  async function handleDelete(id, projName) {
    if (!window.confirm(`Delete project "${projName}"?`)) return;
    try {
      await api.deleteProject(id);
      onProjectsChange();
    } catch (err) {
      setError(err.message);
    }
  }

  function toggleExpand(id) {
    setExpanded(expanded === id ? null : id);
  }

  return (
    <div>
      {/* ── Project list ── */}
      <div className="table-wrap" style={{ marginBottom: '1.25rem' }}>
        <table>
          <thead>
            <tr>
              <th>Project name</th>
              <th style={{ width: 90 }}>Rate / hr</th>
              <th style={{ width: 110 }}>Platform fee</th>
              <th style={{ width: 90 }}>Net rate</th>
              <th style={{ width: 110, textAlign: 'center' }}>Platform work</th>
              <th style={{ width: 36 }}></th>
            </tr>
          </thead>
          <tbody>
            {projects.length === 0 && (
              <tr><td colSpan={6} style={{ textAlign: 'center', color: '#737370', padding: '1.5rem' }}>
                No projects yet — add one below
              </td></tr>
            )}
            {projects.map(p => {
              const isExpanded = expanded === p.id;
              return [
                <tr
                  key={`row-${p.id}`}
                  className={isExpanded ? 'proj-row-expanded' : 'proj-row'}
                  onClick={() => toggleExpand(p.id)}
                  style={{ cursor: 'pointer' }}
                >
                  <td>
                    <CaretIcon open={isExpanded} />
                    {p.name}
                    {p.is_platform_work && <span className="pill-platform">Platform</span>}
                  </td>
                  <td>${Math.round(p.rate_per_hour)}</td>
                  <td>
                    <span className={`fee-pill ${p.platform_fee_pct > 0 ? 'fee-on' : 'fee-off'}`}>
                      {p.platform_fee_pct > 0 ? `${p.platform_fee_pct}%` : '0%'}
                    </span>
                  </td>
                  <td style={{ fontWeight: p.platform_fee_pct > 0 ? 500 : 400 }}>
                    ${Math.round(p.net_rate)}
                  </td>
                  <td style={{ textAlign: 'center' }} onClick={e => e.stopPropagation()}>
                    <input
                      type="checkbox"
                      checked={!!p.is_platform_work}
                      onChange={() => handleTogglePlatform(p)}
                    />
                  </td>
                  <td onClick={e => e.stopPropagation()}>
                    <button className="trash-btn" onClick={() => handleDelete(p.id, p.name)} title="Delete project">
                      <TrashIcon />
                    </button>
                  </td>
                </tr>,
                isExpanded && (
                  <tr key={`exp-${p.id}`} className="proj-row-open-items">
                    <td colSpan={6} style={{ padding: 0, background: '#fafaf9' }}>
                      <OpenItemsPanel projectId={p.id} />
                    </td>
                  </tr>
                ),
              ].filter(Boolean);
            })}
          </tbody>
        </table>
      </div>

      {/* ── Add project form ── */}
      <div className="card">
        <div className="form-title">Add project</div>
        <form onSubmit={handleAdd}>
          <div className="add-proj-row">
            <div className="field">
              <label>Project name</label>
              <input value={name} onChange={e => setName(e.target.value)} placeholder="e.g. Website Redesign" />
            </div>
            <div className="field">
              <label>Rate / hr ($)</label>
              <input type="number" min="0" step="1" value={rate} onChange={e => setRate(e.target.value)} placeholder="0" />
            </div>
            <div className="field">
              <label>Platform fee (%)</label>
              <input type="number" min="0" max="100" step="0.1" value={fee} onChange={e => setFee(e.target.value)} placeholder="10" />
            </div>
            <div className="field">
              <label>Net rate</label>
              <input readOnly value={`$${netRate}${parseFloat(fee) > 0 ? ` (-${fee}%)` : ''}`}
                style={{ background: '#f9f9f8', color: '#737370' }} />
            </div>
            <div className="field" style={{ alignItems: 'flex-start' }}>
              <label>Platform work</label>
              <label className="checkbox-wrap">
                <input
                  type="checkbox"
                  checked={platform}
                  onChange={e => setPlatform(e.target.checked)}
                />
                <span>Hide invoice</span>
              </label>
            </div>
            <div className="field">
              <label>&nbsp;</label>
              <button className="btn-primary" type="submit" disabled={loading}>
                {loading ? 'Adding…' : '+ Add Project'}
              </button>
            </div>
          </div>
          {error && <p className="error-msg" style={{ marginTop: 8 }}>{error}</p>}
        </form>
      </div>

      <p className="hint">Click a project row to manage its open items (tasks shown on the PDF under "Next Week").</p>
    </div>
  );
}
